import { Divider, HStack, Skeleton, Stack, Text, VStack } from '@chakra-ui/react';
import React, { useMemo } from 'react';

import Paper from '@/components/ui/Paper';
import { DomainsEmptyState } from '@/components/ui/Placeholders/DomainsEmptyState';

import { MonthlySettlementGroup, SettlementImport, SettlementImportListItem } from '../types';

import { SettlementImportItem } from './SettlementImportItem';

interface SettlementImportsListProps {
  imports: SettlementImport[];
  isLoading: boolean;
  selectedMonth: number;
  selectedYear: number;
}

export const SettlementImportsList: React.FC<SettlementImportsListProps> = ({
  imports,
  isLoading,
  selectedMonth,
  selectedYear,
}) => {
  const monthlyGroup = useMemo<MonthlySettlementGroup>(() => {
    const items: SettlementImportListItem[] = imports.map((imp) => {
      const totalAmount = Object.values(imp.reportData).reduce(
        (sum, amount) => sum + amount.total,
        0
      );
      const onlineTotal = Object.values(imp.reportData).reduce(
        (sum, amount) => sum + amount.onlineTotal,
        0
      );
      const facilitiesCount = Object.keys(imp.reportData).length;

      return {
        id: imp.id,
        fileName: imp.fileName,
        importDate: imp.importDate,
        totalAmount,
        onlineTotal,
        facilitiesCount,
        reportData: imp.reportData,
      };
    });

    const totalAmount = items.reduce((sum, item) => sum + item.totalAmount, 0);
    const onlineTotal = items.reduce((sum, item) => sum + item.onlineTotal, 0);
    return {
      month: selectedMonth,
      year: selectedYear,
      imports: items,
      totalAmount,
      onlineTotal,
    };
  }, [imports, selectedMonth, selectedYear]);

  const formatMonthYear = (month: number, year: number) => {
    const date = new Date(year, month - 1);
    const monthName = date.toLocaleDateString('pl-PL', { month: 'long' });
    return {
      month: monthName.charAt(0).toUpperCase() + monthName.slice(1),
      year: year.toString(),
    };
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN',
    }).format(amount);
  };

  if (isLoading) {
    return (
      <Stack spacing={4}>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} height="100px" borderRadius="md" />
        ))}
      </Stack>
    );
  }

  if (monthlyGroup.imports.length === 0) {
    return (
      <DomainsEmptyState
        emptyStateMessage={`Brak importów dla ${
          formatMonthYear(selectedMonth, selectedYear).month
        } ${formatMonthYear(selectedMonth, selectedYear).year}`}
      />
    );
  }

  return (
    <Stack spacing={6}>
      <Paper color="black" p={6}>
        <Stack spacing={4}>
          <HStack spacing={2} align="baseline">
            <Text fontSize="3xl" fontWeight="bold" color="purple.700">
              {formatMonthYear(monthlyGroup.month, monthlyGroup.year).month}
            </Text>
            <Text fontSize="xl" color="gray.600">
              {formatMonthYear(monthlyGroup.month, monthlyGroup.year).year}
            </Text>
          </HStack>
          <Divider borderColor="purple.200" />
          <HStack justify="space-between" align="center" flexWrap="wrap" spacing={8}>
            <VStack align="start" spacing={1}>
              <Text
                fontSize="xs"
                color="gray.600"
                textTransform="uppercase"
                letterSpacing="wider"
                fontWeight="medium"
              >
                Liczba importów
              </Text>
              <HStack align="baseline" spacing={1}>
                <Text fontSize="3xl" fontWeight="bold" lineHeight="1">
                  {monthlyGroup.imports.length}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  {monthlyGroup.imports.length === 1
                    ? 'plik'
                    : monthlyGroup.imports.length < 5
                    ? 'pliki'
                    : 'plików'}
                </Text>
              </HStack>
            </VStack>
            <VStack align={{ base: 'start', md: 'center' }} spacing={1}>
              <Text
                fontSize="xs"
                color="gray.600"
                textTransform="uppercase"
                letterSpacing="wider"
                fontWeight="medium"
              >
                Płatności online
              </Text>
              <Text fontSize="3xl" fontWeight="bold" color="green.600" lineHeight="1">
                {formatAmount(monthlyGroup.onlineTotal)}
              </Text>
            </VStack>
            <VStack align={{ base: 'start', md: 'center' }} spacing={1}>
              <Text
                fontSize="xs"
                color="gray.600"
                textTransform="uppercase"
                letterSpacing="wider"
                fontWeight="medium"
              >
                Płatności przelewem
              </Text>
              <Text fontSize="3xl" fontWeight="bold" color="blue.600" lineHeight="1">
                {formatAmount(monthlyGroup.totalAmount - monthlyGroup.onlineTotal)}
              </Text>
            </VStack>
            <VStack align={{ base: 'start', md: 'end' }} spacing={1}>
              <Text
                fontSize="xs"
                color="gray.600"
                textTransform="uppercase"
                letterSpacing="wider"
                fontWeight="medium"
              >
                Łączna kwota
              </Text>
              <Text fontSize="3xl" fontWeight="bold" color="purple.700" lineHeight="1">
                {formatAmount(monthlyGroup.totalAmount)}
              </Text>
            </VStack>
          </HStack>
        </Stack>
      </Paper>

      <Stack spacing={3}>
        {monthlyGroup.imports.map((item) => (
          <SettlementImportItem key={item.id} item={item} />
        ))}
      </Stack>
    </Stack>
  );
};
